import type { UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useIndeedJobSyncVacancies } from "./useIndeedJobSyncVacancies";
import { createVacancySyncCellRenderers } from "../../components/indeed/vacancySyncCellRenderers";
import { registerJobSyncCellRenderersHook } from "../../hooks/cellRenderersHooks";
import { registerJobSyncTableDataHook } from "../../hooks/tableDataHooks";
import { parseVacancySyncState } from "../../utils";

/**
 * Register Indeed-specific hooks for job-sync module.
 * This file is imported at module initialization to register hooks.
 *
 * Note: Registered functions are hooks themselves - they will be called
 * at component level by the generic hooks.
 */

function useIndeedTableDataImpl(params: {
  clientId: string | undefined;
  tenantId: string | undefined;
  page: number;
  pageSize: number;
}) {
  const { clientId, tenantId, page, pageSize } = params;
  const [searchParams] = useSearchParams();
  const syncStateParam = searchParams.get("syncState");
  const syncState = syncStateParam ? parseVacancySyncState(syncStateParam) : null;

  const result = useIndeedJobSyncVacancies({
    clientId: clientId ?? "",
    tenantId: tenantId ?? undefined,
    page: page ?? 1,
    pageSize: pageSize ?? 10,
    syncState,
    integrationName: "indeed",
    enabled: !!clientId,
  });
  
  // Transform to expected format using useMemo to maintain proper typing
  const transformedData = useMemo(() => {
    if (!result.data) return undefined;
    return {
      data: result.data.data ?? [],
      totalRecords: result.data.totalRecords ?? 0,
    };
  }, [result.data]);
  
  // Return transformed query result with proper typing
  // Use double assertion to handle type transformation
  return {
    ...result,
    data: transformedData,
  } as unknown as UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError>;
}

// Register table data hook for Indeed
// Only applies to client detail view - tenant list uses useJobSyncClients instead
registerJobSyncTableDataHook("indeed", (clientId) =>
  clientId ? useIndeedTableDataImpl : undefined);

// Register cell renderers hook for Indeed
// The registered function is a hook that will be called by the generic hook at component level
registerJobSyncCellRenderersHook("indeed", function useIndeedCellRenderers(params) {
  const { clientId } = params;
  const { t, i18n } = useTranslation();
  
  if (!clientId) return undefined;
  
  return createVacancySyncCellRenderers(t, i18n, clientId);
});

// Export hooks for direct use if needed
export { useIndeedJobSyncVacancies } from "./useIndeedJobSyncVacancies";
export { useRepairVacancies } from "./useRepairVacancies";
export { useResyncVacancies } from "./useResyncVacancies";
